import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import SeoStructuredData from '@/components/feature/SeoStructuredData';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory, getHotArticles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = slug ? getArticlesByCategory(slug) : [];
  const hotArticles = getHotArticles();

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-semibold text-foreground-950 mb-2">分类未找到</h1>
            <p className="text-foreground-600 mb-4">抱歉，您访问的分类不存在。</p>
            <Link
              to="/"
              className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SeoStructuredData
        type="WebPage"
        pageTitle={`${category.name} - 久草社区`}
        pageDescription={category.description}
        pageUrl={`https://jiucao.community/category/${category.slug}`}
        breadcrumbItems={[
          { name: '首页', url: 'https://jiucao.community' },
          { name: category.name, url: `https://jiucao.community/category/${category.slug}` },
        ]}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100/70 border-b border-background-200/60">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />
            <div className="flex items-center gap-3 mt-4">
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                <i className={`${category.icon} text-xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">{category.description}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-background-100/50 border-b border-background-200/40">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-3">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 flex-wrap">
              <Link
                to="/category/tech"
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  slug === 'tech'
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-background-50 text-foreground-600 hover:text-foreground-950 border border-background-200/60'
                }`}
              >
                全部
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    slug === cat.slug
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:text-foreground-950 border border-background-200/60'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {categoryArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                    <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-600">该分类暂无文章</p>
                </div>
              )}
            </div>

            <aside className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="bg-background-50 rounded-xl border border-background-200/60 p-5 mb-5">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-1 h-5 rounded-full bg-accent-500"></div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950">
                      全站热门
                    </h3>
                  </div>
                  <div className="divide-y divide-background-200/40">
                    {hotArticles.slice(0, 6).map((article) => (
                      <ArticleCard key={article.id} article={article} variant="compact" />
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}